import type { PaymentKeys, PaymentMethod, Pricing, SavedCard } from '@/types/checkout';
import { convertToDecimalAmount } from './pricing';
import { PaymentMethodEnum, ProviderEnum } from '@/types/common';
import { AppchargeErrorType } from '@/types/payment';
import type { BraintreeTokenizePayload } from '@paypal/react-paypal-js/dist/types/types/braintree/commonsTypes';
import { getPaymentKey } from '@/utils/getPaymentKey';
import { getPaypalScriptOptions } from '@/utils/paymentParams';

/**
 * A vaulted PayPal account is surfaced by the BFF inside the same
 * `savedCards[]` array as cards, tagged with `paymentProvider === 'paypal'`
 * (see checkout-gateway#750). It must NOT be rendered in the saved-cards list:
 * its presence means the buyer can pay one-click with PayPal.
 */
export const isSavedPaypalAccount = (savedCard: SavedCard): boolean =>
  (savedCard.paymentProvider ?? '').toLowerCase() === ProviderEnum.PAYPAL;

/**
 * Splits the BFF `savedCards[]` into actual cards and the (at most one)
 * vaulted PayPal account, so each can drive its own UI.
 */
export const splitSavedInstruments = (
  savedCards: SavedCard[] = []
): { cards: SavedCard[]; paypalAccount?: SavedCard } => {
  const cards: SavedCard[] = [];
  let paypalAccount: SavedCard | undefined;

  for (const savedCard of savedCards) {
    if (isSavedPaypalAccount(savedCard)) {
      paypalAccount = paypalAccount ?? savedCard;
    } else {
      cards.push(savedCard);
    }
  }

  return { cards, paypalAccount };
};

/**
 * Human-readable label for a vaulted PayPal account. psps-management stores the
 * payer email (falling back to the payer name) in `nameOnCard`, so prefer that
 * over card-shaped fields to show the PayPal account email.
 */
export const getSavedPaypalLabel = (account?: SavedCard): string =>
  account?.nameOnCard || account?.cardholderName || account?.cardNumber || account?.cardLastFourDigits || 'PayPal';

/**
 * Privacy-masked label for the saved-PayPal row. When the stored label is an
 * email (the common case — psps-management saves the payer email in
 * `nameOnCard`), keep the first and last character of the local part and mask
 * the rest, e.g. `someone@gmail.com` → `s***e@gmail.com`. Non-email labels
 * (a payer name / generic fallback) are returned unchanged.
 */
export const getMaskedSavedPaypalLabel = (account?: SavedCard): string => {
  const label = getSavedPaypalLabel(account);
  const atIndex = label.indexOf('@');
  if (atIndex <= 0) return label;

  const local = label.slice(0, atIndex);
  const domain = label.slice(atIndex);
  const maskedLocal = local.length <= 2 ? `${local[0]}***` : `${local[0]}***${local[local.length - 1]}`;
  return `${maskedLocal}${domain}`;
};

// PayPal's SDK (and Braintree's PayPal Checkout wrapper) exposes no
// structured error code — every `onError` fires a plain `Error` with a
// free-text message (occasionally a non-Error value). These are the two
// verbatim messages the SDK throws when the shopper closes the popup
// themselves, so they must stay silent rather than surface as a decline.
const PAYPAL_CANCEL_MESSAGE_SUBSTRINGS = ['Detected popup close', 'Window is closed, can not determine type'];

const PAYPAL_CONNECTION_MESSAGE_PATTERNS = [/failed to fetch/i, /network\s*error/i, /load failed/i, /timed?\s*out/i];

/**
 * Classifies a PayPal/Braintree SDK error so the shopper never sees the raw
 * message. `null` means "shopper-initiated cancel" (silent, no error shown),
 * matching `mapAdyenSdkError`'s CANCEL contract. Anything unrecognized
 * (including non-Error throws, where there is truly nothing to key off of)
 * falls back to `GENERIC_DECLINE` — never the raw SDK text.
 */
export const mapPaypalSdkError = (error: unknown): AppchargeErrorType | null => {
  const message = error instanceof Error ? error.message : undefined;
  if (!message) return AppchargeErrorType.GENERIC_DECLINE;

  if (PAYPAL_CANCEL_MESSAGE_SUBSTRINGS.some((substring) => message.includes(substring))) {
    return null;
  }
  if (PAYPAL_CONNECTION_MESSAGE_PATTERNS.some((pattern) => pattern.test(message))) {
    return AppchargeErrorType.CONNECTION;
  }
  return AppchargeErrorType.GENERIC_DECLINE;
};

export const buildPaypalPaymentParams = (pricing: Pricing, shouldVault = false) => {
  const priceBreakdown = pricing.priceBreakdown;
  const amount = convertToDecimalAmount(priceBreakdown?.priceTotal, pricing.multiplier);
  const unitAmount = convertToDecimalAmount(priceBreakdown?.priceNet, pricing.multiplier);
  const unitTaxAmount = convertToDecimalAmount(priceBreakdown?.tax, pricing.multiplier);
  return {
    flow: 'checkout' as const,
    amount,
    currency: pricing.currencyCode,
    // "Checkout with Vault": pairs the one-time sale with a billing agreement so
    // Braintree can vault the PayPal account (returns paypalAccount.token).
    // A plain checkout nonce (requestBillingAgreement: false) is single-use and
    // CANNOT be vaulted — the sale succeeds but no reusable token is created.
    requestBillingAgreement: shouldVault,
    intent: 'capture' as const,
    enableShippingAddress: true,
    shippingAddressEditable: false,
    billingAgreementDescription: 'Description of the billng agreement to display to the customer',
    lineItems: [
      {
        quantity: '1',
        unitAmount,
        kind: 'debit' as const,
        unitTaxAmount,
        url: undefined,
        description: '',
        name: 'Payment',
        productCode: 'Payment',
      },
    ],
  };
};

export const buildPaypalPaymentRequest = (
  tokenizationData: BraintreeTokenizePayload,
  paymentMethodSettings: PaymentMethod,
  shouldSaveCard = false
) => {
  return {
    paymentProvider: paymentMethodSettings.provider,
    receipt_email: tokenizationData.details.email,
    paymentMethodDisplayName: 'Paypal',
    paymentMethodId: paymentMethodSettings.id,
    params: {
      paymentNonce: tokenizationData.nonce,
      shouldSaveCard,
      paymentMethodDetails: {
        email: tokenizationData.details.email,
        firstName: tokenizationData.details.firstName,
        lastName: tokenizationData.details.lastName,
        payerId: tokenizationData.details.payerId,
        countryCode: tokenizationData.details.countryCode,
        billingAddress: {
          countryCode: tokenizationData.details.billingAddress?.countryCode ?? tokenizationData.details.countryCode,
        },
        shippingAddress: {
          line1: tokenizationData.details.shippingAddress?.line1 ?? '',
          city: tokenizationData.details.shippingAddress?.city ?? '',
          countryCode: tokenizationData.details.shippingAddress?.countryCode ?? tokenizationData.details.countryCode,
          postalCode: tokenizationData.details.shippingAddress?.postalCode ?? '',
          state: tokenizationData.details.shippingAddress?.state ?? '',
          recipientName:
            tokenizationData.details.shippingAddress?.recipientName ??
            `${tokenizationData.details.firstName} ${tokenizationData.details.lastName}`,
        },
      },
      fraudDetection: {
        forterTokenCookie: '',
      },
    },
  };
};

/**
 * One-click charge of a vaulted PayPal account. Sends the saved `paymentToken`
 * and the same value as `paymentMethodToken` so psps-management can charge
 * without a PayPal re-auth, same mechanism as a saved card (see
 * psps-management#238).
 */
export const buildSavedPaypalPaymentRequest = (
  savedPaypalAccount: SavedCard,
  paymentMethodSettings: PaymentMethod,
  receiptEmail = ''
) => {
  return {
    paymentProvider: paymentMethodSettings.provider,
    // Prefer the checkout customer's email (same as the saved-card one-click
    // flow); fall back to the vaulted account's stored email (nameOnCard). The
    // gateway rejects an empty receipt_email.
    receipt_email: receiptEmail || savedPaypalAccount.nameOnCard || savedPaypalAccount.cardholderName || '',
    paymentMethodDisplayName: 'Paypal',
    paymentMethodId: paymentMethodSettings.id,
    params: {
      paymentToken: savedPaypalAccount.pspCardIdentifier ?? '',
      paymentMethodToken: savedPaypalAccount.pspCardIdentifier ?? '',
      fraudDetection: {
        forterTokenCookie: '',
      },
    },
  };
};

/**
 * PayPal script options for the checkout method. Prefers the method's sub-MID
 * key. Braintree PayPal falls back to `pks.braintree`; native PayPal falls
 * back to `pks.paypal`.
 */
export const getPaypalScriptOptionsForMethod = (
  paymentMethod: PaymentMethod | undefined,
  paymentKeys: PaymentKeys,
  currencyCode: string
) => {
  const paypalTopLevelKey =
    paymentMethod?.provider === ProviderEnum.BRAINTREE ? paymentKeys.braintree : paymentKeys.paypal;
  const paypalKey = getPaymentKey(paymentMethod?.pspSubMidPk, paypalTopLevelKey, {
    provider: paymentMethod?.provider ?? ProviderEnum.PAYPAL,
    paymentMethod: PaymentMethodEnum.PAYPAL,
  });
  return getPaypalScriptOptions(paypalKey ?? '', currencyCode);
};
